import { z } from 'zod';

export type IdpNotificationSourceStatus = 'OK' | 'UNAVAILABLE';

/**
 * GET /idp/notifications/{dealerId} after normalization (VMS1-6372, VMS1-6492).
 * The spec marks no field as required, so missing values are filled in here and a missing source
 * is treated as UNAVAILABLE.
 */
export interface IdpNotificationResponse {
  /** Unseen Revive (CRM lead) ids. With `sources.revive: UNAVAILABLE` this may be every id. */
  reviveIds: string[];
  /** New Reveal sessions since the seen date (max 30 days). Absent when Reveal could not be fetched. */
  revealCount?: number;
  /**
   * Stored "Reveal seen up to and including" day (yyyyMMdd) for this user and dealer (VMS1-6492).
   * Absent when nothing is stored, when it could not be read, or before the BE ships the field.
   */
  revealSeenDate?: string;
  sources: {
    revive: IdpNotificationSourceStatus;
    reveal: IdpNotificationSourceStatus;
  };
}

const sourceStatusSchema = z.enum(['OK', 'UNAVAILABLE']).optional().default('UNAVAILABLE');

export const idpNotificationResponseSchema = z
  .object({
    reviveIds: z.array(z.string()).optional().default([]),
    revealCount: z.number().int().nonnegative().optional(),
    // A malformed date is dropped rather than failing the whole response — the badge and Revive
    // ids are still usable, and the Reveal dots fall back to the locally stored date.
    revealSeenDate: z
      .string()
      .regex(/^\d{8}$/)
      .optional()
      .catch(undefined),
    sources: z
      .object({ revive: sourceStatusSchema, reveal: sourceStatusSchema })
      .optional()
      .default({ revive: 'UNAVAILABLE', reveal: 'UNAVAILABLE' }),
  })
  .transform((res): IdpNotificationResponse => ({
    reviveIds: res.reviveIds,
    ...(res.revealCount !== undefined && { revealCount: res.revealCount }),
    ...(res.revealSeenDate !== undefined && { revealSeenDate: res.revealSeenDate }),
    sources: res.sources,
  }));
